// This component provides list of all tiles for a given tab

import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import TabList from './TabList';
import {
  actionsToAddTile,
  actionsToCompleteRenameTile,
  actionsToEnableRenameTile,
  actionsToRemoveTile,
  actionsToUpdateSelectedTile,
} from '../../redux/TabTile/TabTileActionsAndMultipleDispatches';

import TileList from './TileList';

import { TileRibbonProps, TileRibbonStateProps } from './TileRibbonInterfaces';
import AddIcon from '@mui/icons-material/Add';
import {
  addChartFilterTabTileName,
  updateChartFilterSelectedGroups,
} from '../../redux/ChartFilterGroup/ChartFilterGroupStateActions';
import Logger from '../../Logger';
import './individualTile.css';
import { Tooltip, Menu, MenuItem, Box } from '@mui/material';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  callQueryAPI,
  updateUserSelection,
  updateUserSelectionColumn,
} from '../../redux/ChartPoperties/ChartControlsActions';
import { useDispatch } from 'react-redux';
import { setColorScheme, setTileThemeId } from '../../redux/ChartPoperties/ChartControlsActions';
import useCombinedColorSchemes from '../../hooks/useCombinedColorSchemes';
import fetchThemeIdByName from '../../hooks/fetchThemeIdByName';
import { NotificationDialog } from '../CommonFunctions/DialogComponents';
import { AlertColor } from '@mui/material';
import { reOrderTileList } from '../../redux/TabTile/TileActions';

const TileRibbon = ({
  // state
  tabTileProps,
  tabState,
  tileState,
  tableData,
  chartProp,
  chartGroup,
  chartControls,

  //props
  tabAreaBackgroundColor,
  iconColor,
  selectedTabBackgroundColor,

  // dispatch
  addTile,
  selectTile,
  enableRenameTile,
  completeRenameTile,
  removeTile,
  addChartFilterTabTileName,
  updateChartFilterSelectedGroups,
  setColorScheme,
}: TileRibbonProps) => {
  const dispatch = useDispatch();
  const addReportFilterGroup = (nextPropKey: string, propKey: string) => {
    var propKey: string = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;
    let selectedDatasetID = chartProp.properties[propKey].selectedDs.id;

    ///	if (!(selectedFilterGroups && selectedFilterGroups.length > 0)) {
    addChartFilterTabTileName(selectedDatasetID, nextPropKey);
    if (chartGroup.groups && Object.keys(chartGroup.groups).length > 0) {
      updateChartFilterSelectedGroups(nextPropKey, chartGroup.tabTile[propKey][0]);
    }
    ///	}
  };

  const allThemes = useCombinedColorSchemes();

  const MAX_TILES = 20;
  const [notification, setNotification] = useState({
    open: false,
    severity: 'warning' as AlertColor,
    message: '',
  });

  const showNotification = (message: string, severity: AlertColor = 'warning') => {
    setNotification({
      open: true,
      severity,
      message,
    });
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  // adding new tile information to store
  const handleAddTile = async () => {
    if (tilesForSelectedTab && tilesForSelectedTab.length >= MAX_TILES) {
      showNotification(`Maximum tile limit reached (${MAX_TILES} tiles per tab)`);
      return;
    }
    let tabObj = tabState.tabs[tabTileProps.selectedTabId];
    let propKey: string = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;
    let nextTileId = tabObj.nextTileId;
    let newTileName = `Tile ${nextTileId}`;
    let newFileId = null;

    addTile(
      tabObj.tabId,
      tabObj.nextTileId,
      tabTileProps.selectedTable,
      chartProp.properties[propKey].selectedDs,
      chartProp.properties[propKey].selectedTable,
    );

    selectTile(tabObj.tabId, newTileName, nextTileId, nextTileId + 1, false, newFileId);

    addReportFilterGroup(`${tabObj.tabId}.${tabObj.nextTileId}`, propKey);

    if (
      chartControls?.properties[propKey]?.rowSecurity?.filterPanels[
        chartControls?.properties?.[propKey]?.rowSecurity?.currentOperator?.toLowerCase()
      ]?.filters.length > 0
    ) {
      dispatch(
        updateUserSelection(
          propKey,
          chartControls?.properties?.[propKey]?.rowSecurity?.currentOperator.toLowerCase(),
          [],
        ),
      );
      dispatch(callQueryAPI(propKey, true));
    }
    if (
      chartControls?.properties[propKey]?.columnSecurity?.filterPanels[
        chartControls?.properties?.[propKey]?.columnSecurity?.currentOperator.toLowerCase()
      ]?.filters.length > 0
    ) {
      dispatch(
        updateUserSelectionColumn(
          propKey,
          chartControls?.properties?.[propKey]?.columnSecurity?.currentOperator.toLowerCase(),
          [],
        ),
      );
      dispatch(callQueryAPI(propKey, true));
    }
    const nextPropKey = `${tabObj.tabId}.${tabObj.nextTileId}`;
    const dashboardState = tabState.tabs[tabObj.tabId]?.dashboardState;

    const shouldInheritTheme = dashboardState?.allTiles || dashboardState?.allTabs;

    if (shouldInheritTheme) {
      const colorScheme = dashboardState?.colorScheme || 'peacock';
      const rawThemeId = dashboardState?.themeId;

      setColorScheme(nextPropKey, colorScheme);

      if (rawThemeId) {
        const matchingTheme = allThemes.find((theme) =>
          theme.isUser ? theme.id === rawThemeId : theme.name === rawThemeId,
        );

        if (matchingTheme?.id) {
          setTileThemeId(nextPropKey, matchingTheme.id);
        } else {
          const fetchedThemeId = await fetchThemeIdByName(rawThemeId);
          if (fetchedThemeId) {
            setTileThemeId(nextPropKey, fetchedThemeId);
          }
        }
      }
    } else {
      const globalDefaultId: string = dashboardState?.defaultThemeId || '';

      const defaultThemeObj = allThemes.find(
        (t) => t.id === globalDefaultId || t.themeId === globalDefaultId,
      );

      if (defaultThemeObj) {
        setColorScheme(nextPropKey, defaultThemeObj.name);
        const themeIdToSet: string = defaultThemeObj.themeId || defaultThemeObj.id || 'peacock';
        setTileThemeId(nextPropKey, themeIdToSet);
      } else {
        // Fallback if no global default is found in the themes list
        setColorScheme(nextPropKey, 'peacock');
        setTileThemeId(nextPropKey, 'peacock');
      }
    }
  };

  const handleSelectTile = (tileId: number, tileName: string, tabId: number, tabName: string) => {
    let tabObj = tabState.tabs[tabTileProps.selectedTabId];
    let nextTileId = tabObj.nextTileId;

    let propKey: string = `${tabId}.${tileId}`;
    let chartObj: any = chartProp.properties[propKey];
    selectTile(tabId, tileName, tileId, nextTileId, false, chartObj.fileId);
    let callAPIForSecurity = false;
    if (tabTileProps.selectedTileId !== tileId) {
      callAPIForSecurity = true;
    }
    if (
      chartControls?.properties[propKey]?.rowSecurity?.filterPanels[
        chartControls?.properties?.[propKey]?.rowSecurity?.currentOperator?.toLowerCase()
      ]?.filters.length > 0
    ) {
      dispatch(
        updateUserSelection(
          propKey,
          chartControls?.properties?.[propKey]?.rowSecurity?.currentOperator.toLowerCase(),
          [],
        ),
      );
      if (callAPIForSecurity) {
        dispatch(callQueryAPI(propKey, true));
      }
    }
    if (
      chartControls?.properties[propKey]?.columnSecurity?.filterPanels[
        chartControls?.properties?.[propKey]?.columnSecurity?.currentOperator.toLowerCase()
      ]?.filters.length > 0
    ) {
      dispatch(
        updateUserSelectionColumn(
          propKey,
          chartControls?.properties?.[propKey]?.columnSecurity?.currentOperator.toLowerCase(),
          [],
        ),
      );
      if (callAPIForSecurity) {
        dispatch(callQueryAPI(propKey, true));
      }
    }
    setTimeout(() => {
      handleClose();
    }, 300);
  };

  const handleRenameTileBegin = (tabId: number, tileId: number) => {
    enableRenameTile(tabId, tileId, true);
  };

  const handleRenameTileComplete = (renameValue: string, tabId: number, tileId: number) => {
    let tabObj = tabState.tabs[tabTileProps.selectedTabId];
    let nextTileId = tabObj.nextTileId;
    completeRenameTile(tabId, tileId, renameValue, nextTileId, false);
  };

  // Selecting which tile to highlight next
  // if we are removing a tile that is currently selected, pick another tile before or after to highlight.
  // Else no change in highlighting tiles

  const handleRemoveTile = async (tabId: number, tileId: number) => {
    const isDashboardActive = tabState.tabs[tabId]?.showDash;
    console.log('isDashboardActive', isDashboardActive);
    let tilesForSelectedTab = tileState.tileList[tabId];

    let numTiles = tilesForSelectedTab.length;
    let tileIndex = tilesForSelectedTab.findIndex((tile) => tile === `${tabId}.${tileId}`);

    let prevSelectedTile = tabTileProps.selectedTileId;
    if (tileId === prevSelectedTile) {
      Logger('info', 'case 1');
      // handle selecting a new tile
      let nextTileId = tabTileProps.nextTileId;
      if (numTiles === 1) {
        Logger('info', 'case 1.1');
        await handleAddTile();
        removeTile(tabId, tileId, tileIndex);
      } else {
        Logger('info', 'case 1.2');
        // if there are more than one tiles
        let selectedTileName = '';
        let selectedTileId = 0;

        if (tileIndex !== 0) {
          Logger('info', 'case 1.2.1');
          let newTileKey: string = tilesForSelectedTab[tileIndex - 1];
          let newTileObj = tileState.tiles[newTileKey];
          selectedTileName = newTileObj.tileName;
          selectedTileId = newTileObj.tileId;
          let propKey: string = `${tabId}.${tileId}`;
          let chartObj: any = chartProp.properties[propKey];
          selectTile(tabId, selectedTileName, selectedTileId, nextTileId, true, chartObj.fileId);
          setTimeout(() => {
            removeTile(tabId, tileId, tileIndex);
          }, 0);
        } else {
          Logger('info', 'case 1.2.2');
          let newTileKey: string = tilesForSelectedTab[tileIndex + 1];
          let newTileObj = tileState.tiles[newTileKey];
          selectedTileName = newTileObj.tileName;
          selectedTileId = newTileObj.tileId;
          let propKey: string = `${tabId}.${selectedTileId}`;
          let chartObj: any = chartProp.properties[propKey];
          selectTile(tabId, selectedTileName, selectedTileId, nextTileId, true, chartObj.fileId);
          setTimeout(() => {
            removeTile(tabId, tileId, tileIndex);
          }, 0);
        }
        // let propKey: string = `${tabId}.${tileId}`;
        // let chartObj: any = chartProp.properties[propKey];
        // selectTile(
        // 	tabId,
        // 	selectedTileName,
        // 	selectedTileId,
        // 	nextTileId,
        // 	false,
        // 	chartObj.fileId
        // );
      }
    } else {
      Logger('info', 'case 2');
      removeTile(tabId, tileId, tileIndex);
    }
  };

  let tilesForSelectedTab: any[] = tileState.tileList[tabTileProps.selectedTabId];

  const ITEM_HEIGHT = 34;
  const [anchorEl, setAnchorEl] = useState<any>(null);
  const tileOpen = Boolean(anchorEl);
  const tileWrapperRef = useRef<HTMLDivElement>(null);

  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const checkOverflow = () => {
    if (tileWrapperRef.current && contentRef.current) {
      const bufferPixels = 5;

      const isContentOverflowing =
        contentRef.current.scrollWidth > tileWrapperRef.current.clientWidth - bufferPixels;

      setIsOverflowing(isContentOverflowing);
    }
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();
    });

    if (tileWrapperRef.current) {
      resizeObserver.observe(tileWrapperRef.current);
    }
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const handleTileScroll = (step: number) => {
    tileWrapperRef.current!.scrollLeft += step;
  };

  const handleTileScrollOnNewTileAdd = () => {
    setTimeout(() => {
      if (contentRef.current && tileWrapperRef.current) {
        tileWrapperRef.current.scrollLeft = contentRef.current.scrollWidth;
      }
    }, 50);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleReOrderTileList = (tabId: number, moveAction: string, moveItemInd: number) => {
    dispatch(reOrderTileList(tabId, moveAction, moveItemInd));
  };

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <div
        style={{
          display: 'flex',
          overflowX: 'hidden',
          alignItems: 'center',
        }}
      >
        <Tooltip
          title="Display Tile List"
          slotProps={{
            tooltip: {
              sx: {
                fontSize: '12px',
              },
            },
          }}
        >
          <button
            style={{
              margin: '0 4px',
              display: 'flex',
              border: 'none',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: tabAreaBackgroundColor,
              color: iconColor,
              padding: '5px',
              minWidth: '1rem',
            }}
            onClick={(e) => {
              setAnchorEl(e.currentTarget);
            }}
          >
            <KeyboardArrowUpIcon style={{ fontSize: '20px', color: iconColor }} />
          </button>
        </Tooltip>
        <Menu
          id="long-menu"
          MenuListProps={{
            'aria-labelledby': 'long-button',
          }}
          anchorEl={anchorEl}
          open={tileOpen}
          onClose={handleClose}
          slotProps={{
            paper: {
              style: {
                minHeight: ITEM_HEIGHT * 4.5,
                maxHeight: ITEM_HEIGHT * 12.3,
                width: '26ch',
                margin: '-26px 0px 0px -70px',
                padding: '0',
                boxShadow: `${iconColor === '#ffffff' ? '0px 2px 4px rgba(184, 159, 159, 0.5)' : '0px 2px 4px rgba(0, 0, 0, 0.2)'}`,
                backgroundColor: tabAreaBackgroundColor,
              },
            },
          }}
        >
          <TileList
            reOrderTileList={handleReOrderTileList}
            tiles={tilesForSelectedTab}
            tileState={tileState}
            tabTileProps={tabTileProps}
            handleClose={handleClose}
            handleRenameTileBegin={handleRenameTileBegin}
            handleRenameTileComplete={handleRenameTileComplete}
            handleSelectTile={handleSelectTile}
            handleRemoveTile={handleRemoveTile}
            tabAreaBackgroundColor={tabAreaBackgroundColor}
            iconColor={iconColor}
            selectedTabBackgroundColor={selectedTabBackgroundColor}
            inPopup={true}
            MaxTiles={MAX_TILES}
            tileListOpen={tileOpen}
          />
        </Menu>
        <div style={{ display: 'flex', overflow: 'hidden', width: '100%' }} ref={tileWrapperRef}>
          <TileList
            tiles={tilesForSelectedTab}
            reOrderTileList={handleReOrderTileList}
            tileState={tileState}
            tabTileProps={tabTileProps}
            handleRenameTileBegin={handleRenameTileBegin}
            handleRenameTileComplete={handleRenameTileComplete}
            handleSelectTile={handleSelectTile}
            handleRemoveTile={handleRemoveTile}
            tabAreaBackgroundColor={tabAreaBackgroundColor}
            iconColor={iconColor}
            selectedTabBackgroundColor={selectedTabBackgroundColor}
            childRef={contentRef}
            MaxTiles={MAX_TILES}
          />
          <span
            style={{
              visibility: isOverflowing ? 'hidden' : 'visible',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            title={
              tilesForSelectedTab && tilesForSelectedTab.length >= MAX_TILES
                ? `Maximum ${MAX_TILES} tiles allowed`
                : 'Create a new tile'
            }
            // className="plusTile commonTile"
            className="plusTile"
            onClick={() => {
              handleAddTile();
              setTimeout(() => {
                handleTileScroll(100);
                handleTileScroll(100);
              }, 400);
            }}
          >
            <AddIcon sx={{ fontSize: '16px', color: iconColor }} />
          </span>
        </div>
      </div>
      <div style={{ display: 'flex' }}>
        {
          <div
            style={{
              visibility: isOverflowing ? 'visible' : 'hidden',
              marginLeft: '4px',
              gap: '4px',
              display: 'flex',
              justifyContent: 'flex-end',
              overflow: 'hidden',
              alignItems: 'center',
            }}
          >
            {
              <span
                style={{
                  visibility: isOverflowing ? 'visible' : 'hidden',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '1.7rem',
                  color: iconColor,
                }}
                title="Create a new tile"
                // className="plusTile commonTile"
                className="plusTile"
                onClick={() => {
                  handleAddTile();
                  handleTileScrollOnNewTileAdd();
                }}
              >
                <AddIcon sx={{ fontSize: '16px' }} />
              </span>
            }
            <ArrowLeftIcon
              style={{
                cursor: 'pointer',
                color: iconColor,
              }}
              onClick={() => handleTileScroll(-100)}
            />
            <ArrowRightIcon
              style={{
                cursor: 'pointer',
                color: iconColor,
                marginRight: '4px',
              }}
              onClick={() => handleTileScroll(100)}
            />
          </div>
        }
      </div>
      <NotificationDialog
        openAlert={notification.open}
        severity={notification.severity}
        testMessage={notification.message}
        onCloseAlert={handleCloseNotification}
      />
    </div>
  );
};

const mapStateToProps = (state: TileRibbonStateProps) => {
  return {
    tabTileProps: state.tabTileProps,
    tabState: state.tabState,
    tileState: state.tileState,
    tableData: state.tableData,
    chartProp: state.chartProperties,
    chartGroup: state.chartFilterGroup,
    chartControls: state.chartControls,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return {
    updateChartFilterSelectedGroups: (groupId: string, filters: any) =>
      dispatch(updateChartFilterSelectedGroups(groupId, filters)),
    addTile: (
      tabId: number,
      nextTileId: number,
      table: any,
      selectedDataset: any,
      selectedTables: any,
    ) =>
      dispatch(
        actionsToAddTile({
          tabId,
          nextTileId,
          table,
          fromTab: false,
          selectedDs: selectedDataset,
          selectedTablesInDs: selectedTables,
        }),
      ),

    selectTile: (
      tabId: number,
      tileName: string,
      tileId: number,
      nextTileId: number,
      fromTab: any,
      fileId: any,
    ) =>
      dispatch(actionsToUpdateSelectedTile(tabId, tileName, tileId, nextTileId, fromTab, fileId)),

    enableRenameTile: (tabId: number, tileId: number, isTrue: boolean) =>
      dispatch(actionsToEnableRenameTile(tabId, tileId, isTrue)),

    completeRenameTile: (
      tabId: number,
      tileId: number,
      renameValue: string,
      nextTileId: number,
      isTrue: boolean,
    ) => dispatch(actionsToCompleteRenameTile(tabId, tileId, renameValue, nextTileId, isTrue)),

    removeTile: (tabId: number, tileId: number, tileIndex: number) =>
      dispatch(actionsToRemoveTile(tabId, tileId, tileIndex)),
    addChartFilterTabTileName: (selectedDatasetID: string, tabTileName: string) =>
      dispatch(addChartFilterTabTileName(selectedDatasetID, tabTileName)),
    setColorScheme: (propKey: string, color: string) => dispatch(setColorScheme(propKey, color)),
    // showDashBoard: (tabId, showDash) => dispatch(actions.setShowDashBoard(tabId, showDash)),
    setTileThemeId: (propKey: string, themeId: string) =>
      dispatch(setTileThemeId(propKey, themeId)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(TileRibbon);
